import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Users, Calendar, Briefcase, TrendingUp, DollarSign, Activity, CheckCircle2, X,
  ShieldCheck, Inbox, Settings, RefreshCw, FileText, ArrowUpRight, Clock, Search,
  Bell, ListChecks, AlertCircle, ScrollText,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  TasksTab, StaffingRequestsTab, ChecklistSubmissionsTab,
  NotificationsTab, AuditLogsTab, OverviewCards,
} from "./AutomationTabs";

/* ------------------------------------------------------------------ */
/*  Admin Panel — analytics + user / credential / lead controls       */
/*  Tabs: Analytics · Users · Credentials · Leads · System Health      */
/* ------------------------------------------------------------------ */

const TABS = [
  { key: "analytics",   label: "Analytics",   Icon: TrendingUp },
  { key: "tasks",       label: "Tasks",       Icon: ListChecks },
  { key: "staffing",    label: "Staffing",    Icon: Inbox },
  { key: "checklists",  label: "Checklists",  Icon: ShieldCheck },
  { key: "users",       label: "Users",       Icon: Users },
  { key: "credentials", label: "Credentials", Icon: ShieldCheck },
  { key: "leads",       label: "Leads",       Icon: Inbox },
  { key: "notifications", label: "Notifications", Icon: Bell },
  { key: "audit",       label: "Audit",       Icon: ScrollText },
  { key: "health",      label: "System",      Icon: Settings },
];

const STATUS_STYLES = {
  active:    "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending:   "bg-amber-50 text-amber-700 border-amber-200",
  suspended: "bg-red-50 text-trital-red border-red-200",
  approved:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected:  "bg-red-50 text-trital-red border-red-200",
  new:       "bg-amber-50 text-amber-700 border-amber-200",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  qualified: "bg-trital-medical-blue-soft text-trital-navy border-trital-navy/20",
  closed:    "bg-slate-100 text-slate-700 border-slate-200",
};

export default function AdminPanel() {
  const [tab, setTab] = useState("analytics");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [creds, setCreds] = useState([]);
  const [leads, setLeads] = useState([]);
  const [pubStats, setPubStats] = useState(null);
  const [overview, setOverview] = useState(null);

  const [userFilter, setUserFilter]  = useState("");
  const [credFilter, setCredFilter]  = useState("pending");
  const [leadFilter, setLeadFilter]  = useState("all");
  const [userQ, setUserQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, u, c, l, ps, ov] = await Promise.all([
        api.get("/admin/analytics"),
        api.get("/admin/users", { params: userFilter ? { status: userFilter } : {} }),
        api.get("/credentials", { params: credFilter === "all" ? {} : { status: credFilter } }),
        api.get("/admin/leads", { params: leadFilter === "all" ? {} : { status: leadFilter } }),
        api.get("/public/stats"),
        api.get("/admin/overview"),
      ]);
      setStats(a.data); setUsers(u.data); setCreds(c.data); setLeads(l.data); setPubStats(ps.data); setOverview(ov.data);
    } catch (e) {
      toast.error("Couldn't load admin data", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userFilter, credFilter, leadFilter]);

  const setUserStatus = async (id, status) => {
    try {
      await api.post(`/admin/users/${id}/status`, { status });
      toast.success(`User → ${status}`);
      load();
    } catch (e) { toast.error("Couldn't update user", { description: e?.response?.data?.detail || e.message }); }
  };
  const credAction = async (id, action) => {
    try {
      await api.post(`/credentials/${id}/action`, { action });
      toast.success(`Credential ${action}d`);
      load();
    } catch (e) { toast.error("Action failed", { description: e?.response?.data?.detail || e.message }); }
  };
  const setLeadStatus = async (id, status) => {
    try {
      await api.post(`/admin/leads/${id}/status`, { status });
      toast.success(`Lead → ${status}`);
      load();
    } catch (e) { toast.error("Couldn't update lead", { description: e?.response?.data?.detail || e.message }); }
  };

  const filteredUsers = useMemo(() => {
    if (!userQ) return users;
    const ql = userQ.toLowerCase();
    return users.filter((u) => (u.email || "").toLowerCase().includes(ql) || (u.name || "").toLowerCase().includes(ql) || (u.role || "").includes(ql));
  }, [userQ, users]);

  return (
    <div className="space-y-6" data-testid="admin-panel">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-trital-red">Platform admin</div>
          <h1 className="mt-1 font-display font-black text-3xl lg:text-4xl text-slate-900 tracking-tight">Analytics & Controls</h1>
          <p className="text-slate-600 text-sm mt-1">Suspend / reactivate users, approve credentials, manage the lead inbox, and watch system health.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-slate-300 text-[12.5px] font-semibold text-slate-700 hover:border-trital-navy hover:bg-slate-50" data-testid="admin-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} strokeWidth={1.8} /> Refresh
        </button>
      </header>

      {/* Tabs */}
      <nav className="flex gap-1 border-b border-slate-200 overflow-x-auto" role="tablist" data-testid="admin-tabs">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-3 -mb-px border-b-2 text-[13px] font-semibold whitespace-nowrap transition-colors ${
                active ? "border-trital-red text-trital-navy" : "border-transparent text-slate-600 hover:text-trital-navy"
              }`}
              data-testid={`admin-tab-${t.key}`}
            >
              <t.Icon className="w-4 h-4" strokeWidth={1.8} /> {t.label}
            </button>
          );
        })}
      </nav>

      {tab === "analytics" && <AnalyticsTab stats={stats} pub={pubStats} overview={overview} />}
      {tab === "tasks" && <TasksTab />}
      {tab === "staffing" && <StaffingRequestsTab />}
      {tab === "checklists" && <ChecklistSubmissionsTab />}
      {tab === "users" && <UsersTab users={filteredUsers} q={userQ} setQ={setUserQ} filter={userFilter} setFilter={setUserFilter} onSetStatus={setUserStatus} />}
      {tab === "credentials" && <CredentialsTab creds={creds} filter={credFilter} setFilter={setCredFilter} onAction={credAction} />}
      {tab === "leads" && <LeadsTab leads={leads} filter={leadFilter} setFilter={setLeadFilter} onStatus={setLeadStatus} />}
      {tab === "notifications" && <NotificationsTab />}
      {tab === "audit" && <AuditLogsTab />}
      {tab === "health" && <HealthTab pub={pubStats} stats={stats} loading={loading} />}
    </div>
  );
}

/* ============================================================== */

function AnalyticsTab({ stats, pub, overview }) {
  if (!stats) return <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">Loading analytics…</div>;
  const cards = [
    { Icon: Users, label: "Active Clinicians", value: stats.active_clinicians, color: "navy" },
    { Icon: Activity, label: "Facilities", value: stats.facilities, color: "blue" },
    { Icon: Calendar, label: "Open Shifts", value: stats.open_shifts, color: "red" },
    { Icon: CheckCircle2, label: "Filled Shifts", value: stats.filled_shifts, color: "green" },
    { Icon: Briefcase, label: "Open Jobs", value: stats.jobs_open, color: "navy" },
    { Icon: TrendingUp, label: "Placement Rate", value: `${stats.placement_rate}%`, color: "blue" },
    { Icon: DollarSign, label: "Weekly Revenue Est.", value: `$${(stats.revenue_estimate_weekly || 0).toLocaleString()}`, color: "green" },
    { Icon: Activity, label: "Applications", value: stats.applications, color: "red" },
  ];
  const palette = {
    navy:  "bg-trital-medical-blue-soft text-trital-navy",
    blue:  "bg-blue-50 text-blue-700",
    red:   "bg-red-50 text-trital-red",
    green: "bg-emerald-50 text-emerald-700",
  };
  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Operations overview cards (per spec) */}
      {overview && <OverviewCards data={overview} />}

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <motion.div
            key={c.label}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.03 }}
            className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card"
            data-testid={`analytics-${c.label.toLowerCase().replace(/\s+|\./g, "-")}`}
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${palette[c.color]}`}><c.Icon className="w-5 h-5" strokeWidth={1.8} /></div>
            <div className="mt-3 font-display font-black text-3xl text-slate-900 tabular-nums">{c.value}</div>
            <div className="overline text-slate-500 mt-1">{c.label}</div>
          </motion.div>
        ))}
      </div>

      {/* Public stats cross-check */}
      <div className="rounded-2xl bg-trital-mesh border border-slate-200 p-6" data-testid="analytics-public-cross">
        <div className="overline text-trital-red">Public-facing stats (homepage parity)</div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-[13px]">
          <Stat k="Clinicians (total)" v={pub?.clinicians ?? "—"} />
          <Stat k="Facilities (total)" v={pub?.facilities ?? "—"} />
          <Stat k="Shifts (total)" v={pub?.shifts ?? "—"} />
          <Stat k="Jobs (total)" v={pub?.jobs ?? "—"} />
        </div>
      </div>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500 font-bold">{k}</div>
      <div className="mt-0.5 font-display font-black text-trital-navy text-xl tabular-nums">{v}</div>
    </div>
  );
}

function UsersTab({ users, q, setQ, filter, setFilter, onSetStatus }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 shadow-card overflow-hidden" data-testid="users-tab">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.8} />
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, role"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:border-trital-navy"
            data-testid="users-search"
          />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-slate-200 text-[13px]" data-testid="users-status-filter">
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="suspended">Suspended</option>
        </select>
        <span className="ml-auto text-[12px] text-slate-500">{users.length} users</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Role</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.map((u) => (
              <tr key={u.id} className="hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3 font-semibold text-slate-900">{u.name}</td>
                <td className="px-5 py-3 text-slate-600">{u.email}</td>
                <td className="px-5 py-3">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{u.role}</span>
                </td>
                <td className="px-5 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLES[u.status] || "bg-slate-100 text-slate-700"}`}>{u.status}</span>
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex gap-1.5 justify-end">
                    {u.status !== "active" && (
                      <button onClick={() => onSetStatus(u.id, "active")} className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold hover:bg-emerald-700 hover:text-white" data-testid={`user-activate-${u.id}`}>Activate</button>
                    )}
                    {u.status !== "suspended" && (
                      <button onClick={() => onSetStatus(u.id, "suspended")} className="px-2.5 py-1 rounded-full bg-red-50 text-trital-red text-[11px] font-bold hover:bg-trital-red hover:text-white" data-testid={`user-suspend-${u.id}`}>Suspend</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">No users match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CredentialsTab({ creds, filter, setFilter, onAction }) {
  return (
    <div className="space-y-4" data-testid="admin-credentials-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
              filter === s ? "bg-trital-navy text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-trital-navy"
            }`}
            data-testid={`admin-creds-filter-${s}`}
          >
            {s === "all" ? "All" : s}
          </button>
        ))}
        <span className="ml-auto text-[12px] text-slate-500">{creds.length} credentials</span>
      </div>
      {creds.length === 0 ? (
        <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-10 text-center text-slate-500">No credentials in this view.</div>
      ) : (
        <div className="space-y-3">
          {creds.map((c) => (
            <div key={c.id} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card grid lg:grid-cols-[1.5fr_1fr_auto] gap-4 items-center" data-testid={`admin-cred-${c.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLES[c.status] || "bg-slate-100 text-slate-700"}`}>{c.status}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{c.type}</span>
                </div>
                <div className="mt-2 font-display font-bold text-slate-900 truncate">{c.user_name}</div>
                <div className="mt-1 text-[12.5px] text-slate-600 inline-flex items-center gap-3 flex-wrap">
                  {c.issuer && <span>{c.issuer}</span>}
                  {c.number && <span className="font-mono text-[11.5px] text-slate-500">#{c.number}</span>}
                  {c.expires_at && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={1.8} /> Expires {c.expires_at?.slice(0, 10)}</span>}
                </div>
              </div>
              <div className="text-[12px] text-slate-500">
                {c.file_url ? (
                  <a href={c.file_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-trital-navy font-semibold hover:underline">
                    <FileText className="w-3.5 h-3.5" strokeWidth={1.8} /> View document <ArrowUpRight className="w-3 h-3" strokeWidth={1.8} />
                  </a>
                ) : <span className="text-slate-400">No file</span>}
                {c.reviewed_by_name && <div className="mt-1 text-[11px]">Reviewed by {c.reviewed_by_name}</div>}
              </div>
              {c.status === "pending" && (
                <div className="flex gap-1.5">
                  <button onClick={() => onAction(c.id, "approve")} className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-[12px] font-bold hover:bg-emerald-700 hover:text-white inline-flex items-center gap-1" data-testid={`admin-cred-approve-${c.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> Approve
                  </button>
                  <button onClick={() => onAction(c.id, "reject")} className="px-3 py-1.5 rounded-full bg-red-50 text-trital-red text-[12px] font-bold hover:bg-trital-red hover:text-white inline-flex items-center gap-1" data-testid={`admin-cred-reject-${c.id}`}>
                    <X className="w-3.5 h-3.5" strokeWidth={2} /> Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LeadsTab({ leads, filter, setFilter, onStatus }) {
  return (
    <div className="space-y-4" data-testid="admin-leads-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {["all", "new", "contacted", "qualified", "closed"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
              filter === s ? "bg-trital-navy text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-trital-navy"
            }`}
            data-testid={`admin-leads-filter-${s}`}
          >
            {s === "all" ? "All" : s}
          </button>
        ))}
        <span className="ml-auto text-[12px] text-slate-500">{leads.length} leads</span>
      </div>
      {leads.length === 0 ? (
        <div className="rounded-2xl bg-slate-50 border border-dashed border-slate-300 p-10 text-center text-slate-500">No leads in this view.</div>
      ) : (
        <div className="rounded-2xl bg-white border border-slate-200 shadow-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3">Name / Org</th>
                <th className="text-left px-5 py-3">Source</th>
                <th className="text-left px-5 py-3">Email</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-left px-5 py-3">Date</th>
                <th className="text-right px-5 py-3">Move to</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leads.map((l) => (
                <tr key={l.id} className="hover:bg-slate-50" data-testid={`admin-lead-row-${l.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-semibold text-slate-900">{l.name || "—"}</div>
                    {l.organization && <div className="text-[11.5px] text-slate-500">{l.organization}</div>}
                  </td>
                  <td className="px-5 py-3"><span className="text-[10px] px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy font-bold uppercase tracking-wider">{l.source || "other"}</span></td>
                  <td className="px-5 py-3 text-slate-600 truncate max-w-[180px]">{l.email}</td>
                  <td className="px-5 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] uppercase tracking-wider font-bold ${STATUS_STYLES[l.status] || "bg-slate-100 text-slate-700"}`}>{l.status || "new"}</span></td>
                  <td className="px-5 py-3 text-[12px] text-slate-500">{l.created_at?.slice(0, 10)}</td>
                  <td className="px-5 py-3 text-right">
                    <select
                      value={l.status || "new"}
                      onChange={(e) => onStatus(l.id, e.target.value)}
                      className="px-2 py-1 rounded-md border border-slate-200 text-[12px] focus:outline-none focus:border-trital-navy"
                      data-testid={`admin-lead-status-${l.id}`}
                    >
                      <option value="new">new</option>
                      <option value="contacted">contacted</option>
                      <option value="qualified">qualified</option>
                      <option value="closed">closed</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HealthTab({ pub, stats, loading }) {
  const ts = new Date().toLocaleString();
  const items = [
    { label: "API responding",      ok: !loading && !!pub,    detail: !loading && pub ? `Public stats live · ${pub.clinicians ?? 0} clinicians` : "Awaiting…" },
    { label: "Admin analytics",     ok: !!stats,              detail: stats ? `${stats.applications ?? 0} applications · ${stats.placement_rate ?? 0}% placement` : "—" },
    { label: "Open shifts",         ok: (stats?.open_shifts ?? 0) >= 0, detail: `${stats?.open_shifts ?? 0} open · ${stats?.filled_shifts ?? 0} filled` },
    { label: "Last refresh",        ok: true, detail: ts },
  ];
  return (
    <div className="space-y-4" data-testid="admin-health-tab">
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((i) => (
          <div key={i.label} className="rounded-2xl bg-white border border-slate-200 p-5 shadow-card flex items-start gap-4" data-testid={`health-${i.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${i.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-trital-red"}`}>
              {i.ok ? <CheckCircle2 className="w-5 h-5" strokeWidth={2} /> : <X className="w-5 h-5" strokeWidth={2} />}
            </div>
            <div>
              <div className="font-display font-bold text-[15px] text-slate-900">{i.label}</div>
              <div className="text-[12.5px] text-slate-600 mt-0.5">{i.detail}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl bg-trital-mesh border border-slate-200 p-6">
        <div className="overline text-trital-red">Quick links</div>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <a href="/sitemap-index.xml" target="_blank" rel="noopener noreferrer" className="text-[13px] text-trital-navy hover:underline inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" strokeWidth={1.8} /> sitemap-index.xml <ArrowUpRight className="w-3 h-3" strokeWidth={1.8} /></a>
          <a href="/robots.txt" target="_blank" rel="noopener noreferrer" className="text-[13px] text-trital-navy hover:underline inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" strokeWidth={1.8} /> robots.txt <ArrowUpRight className="w-3 h-3" strokeWidth={1.8} /></a>
          <a href="/api/public/stats" target="_blank" rel="noopener noreferrer" className="text-[13px] text-trital-navy hover:underline inline-flex items-center gap-1.5"><Activity className="w-3.5 h-3.5" strokeWidth={1.8} /> /api/public/stats <ArrowUpRight className="w-3 h-3" strokeWidth={1.8} /></a>
          <a href="/api/public/shifts" target="_blank" rel="noopener noreferrer" className="text-[13px] text-trital-navy hover:underline inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" strokeWidth={1.8} /> /api/public/shifts <ArrowUpRight className="w-3 h-3" strokeWidth={1.8} /></a>
        </div>
      </div>
    </div>
  );
}
